import React, { useEffect } from 'react';
import { Mail, User, Building2, Phone, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useCalculatorEmailComposer } from '../hooks/useCalculatorEmailComposer';
import { CalculatorReportEmailPreview } from './CalculatorReportEmailPreview';
import { CalculatorEmailHistoryPanel } from './CalculatorEmailHistoryPanel';
import { useToast } from '@/hooks/use-toast';
import type { MortgageData } from '@/types/calculator';
import type { BuydownScenario } from '@/components/calculator/results/utils/buydownCalculations';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';

interface CalculatorReportEmailComposerProps {
  mortgageData: MortgageData;
  buydownScenarios?: BuydownScenario[];
  formData?: EnhancedUnifiedFormData;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CalculatorReportEmailComposer: React.FC<CalculatorReportEmailComposerProps> = ({
  mortgageData,
  buydownScenarios = [],
  formData,
  onSuccess,
  onCancel,
}) => {
  const { toast } = useToast();
  const {
    recipient,
    setRecipient,
    sender,
    setSender,
    customMessage,
    setCustomMessage,
    isSending,
    sendEmail,
    resetForm,
    loadFromHistory,
  } = useCalculatorEmailComposer({ mortgageData, buydownScenarios, formData });

  const handleLoadFromHistory = (data: Parameters<typeof loadFromHistory>[0]) => {
    loadFromHistory(data);
    toast({
      title: 'Contact loaded',
      description: `Loaded ${data.recipient.name}'s information`,
    });
  };

  // Load sender info from localStorage on mount
  useEffect(() => {
    const savedSender = localStorage.getItem('calculator-email-sender');
    if (savedSender) {
      try {
        const parsed = JSON.parse(savedSender);
        setSender(parsed);
      } catch (e) {
        console.error('Failed to parse saved sender info');
      }
    }
  }, [setSender]);

  // Save sender info to localStorage when it changes
  useEffect(() => {
    if (sender.name || sender.email) {
      localStorage.setItem('calculator-email-sender', JSON.stringify(sender));
    }
  }, [sender]);

  const handleSend = async () => {
    const success = await sendEmail();
    if (success) {
      resetForm();
      onSuccess?.();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 max-h-[70vh] overflow-hidden">
      {/* Form Section */}
      <div className="space-y-6 overflow-y-auto pr-2">
        {/* Recipient Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Recipient Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-name">Name *</Label>
              <Input
                id="recipient-name"
                placeholder="John Smith"
                value={recipient.name}
                onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Email *</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="john@example.com"
                value={recipient.email}
                onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Sender Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Your Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sender-name">Your Name *</Label>
              <Input
                id="sender-name"
                placeholder="Jane Doe"
                value={sender.name}
                onChange={(e) => setSender({ ...sender, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender-email">Your Email *</Label>
              <Input
                id="sender-email"
                type="email"
                placeholder="jane@company.com"
                value={sender.email}
                onChange={(e) => setSender({ ...sender, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender-phone">Phone (optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="sender-phone"
                  placeholder="(555) 123-4567"
                  className="pl-9"
                  value={sender.phone}
                  onChange={(e) => setSender({ ...sender, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender-company">Company (optional)</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="sender-company"
                  placeholder="ABC Mortgage"
                  className="pl-9"
                  value={sender.company}
                  onChange={(e) => setSender({ ...sender, company: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="sender-nmls">NMLS# (optional)</Label>
              <Input
                id="sender-nmls"
                placeholder="123456"
                value={sender.nmls}
                onChange={(e) => setSender({ ...sender, nmls: e.target.value })}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Custom Message */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">
            Personal Message (optional)
          </h3>
          <Textarea
            placeholder="Add a personal note to include in the email..."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            rows={3}
          />
        </div>

        {/* Report Content Info */}
        <div className="bg-muted/50 rounded-lg p-4 text-sm">
          <p className="font-medium mb-2">Report includes:</p>
          <ul className="text-muted-foreground space-y-1">
            <li>• Mortgage summary & monthly payment breakdown</li>
            <li>• Closing costs & cash to close</li>
            <li>• Risk assessment & analytics</li>
            {buydownScenarios.length > 0 && <li>• Buydown analysis & builder incentives</li>}
            <li>• Long-term financial projections</li>
            <li>• Optimization opportunities</li>
            {formData && (
              <>
                <li>• Loan program recommendations</li>
                <li>• Financial health score & action items</li>
              </>
            )}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isSending} className="flex-1">
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Report
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Preview Section */}
      <div className="hidden lg:block border-l pl-6 overflow-y-auto">
        <h3 className="text-lg font-semibold text-foreground mb-4">Email Preview</h3>
        <CalculatorReportEmailPreview
          mortgageData={mortgageData}
          hasBuydownData={buydownScenarios.length > 0}
          hasSupremeIntelligence={!!formData}
        />
      </div>

      {/* History Section */}
      <div className="hidden xl:block border-l pl-6 overflow-hidden">
        <CalculatorEmailHistoryPanel onLoadHistory={handleLoadFromHistory} />
      </div>
    </div>
  );
};
